import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '@/contexts/AuthContext';
import * as cryptoUtils from '@/lib/cryptoUtils';
import PasswordList, { PasswordEntry } from '@/components/Vault/PasswordList';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import MasterkeyForm from '@/components/Auth/MasterkeyForm';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import * as secureStorage from '@/lib/secureStorage';
import crypto from 'crypto';

const Vault: React.FC = () => {
  const { isAuthenticated, vaultUnlocked, masterKey, user, unlockVault } = useAuth();
  const { toast } = useToast();
  const [passwords, setPasswords] = useState<PasswordEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<PasswordEntry | null>(null);
  const [decryptedPasswords, setDecryptedPasswords] = useState<Record<string, string>>({});

  // Check vault status on mount and when auth state changes
  useEffect(() => {
    if (isAuthenticated && !vaultUnlocked) {
      console.log('O cofre está destrancado, a carregar palavra-passes...');
      setLoading(false);
    } else if (isAuthenticated && vaultUnlocked && masterKey) {
      console.log('O cofre está destrancado, a carregar palavra-passes...');
      setLoading(true);
    }
  }, [isAuthenticated, vaultUnlocked, masterKey]);

  // Add this effect to monitor vault status
  useEffect(() => {
    const checkVaultStatus = async () => {
      if (!user) {
        return;
      }

      // Check if master key is available in secure storage
      const storedMasterKey = secureStorage.secureGet('masterKey');
      const hasMasterKey = !!storedMasterKey;
      
      // If we have a master key, load passwords
      if (hasMasterKey) {
        await loadPasswords();
      }
    };

    checkVaultStatus();
  }, [user]);

  // Load passwords from Firestore
  const loadPasswords = async () => {
    try {
      if (!user || !vaultUnlocked) {
        setPasswords([]);
        return;
      }

      // Get the master key from secure storage
      const masterKey = secureStorage.secureGet('masterKey');
      if (!masterKey) {
        console.error('Chave mestre não encontrada no armazenamento seguro');
        setPasswords([]);
        return;
      }

      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();

      if (!userData || !userData.vault) {
        setPasswords([]);
        return;
      }

      // Decrypt the vault data
      const decryptedData = await cryptoUtils.decryptData(userData.vault, masterKey);
      const vaultData = JSON.parse(decryptedData);

      if (!vaultData.passwords || !Array.isArray(vaultData.passwords)) {
        setPasswords([]);
        return;
      }

      setPasswords(vaultData.passwords);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar senpalavra-passes:', error);
      setPasswords([]);
      setError('Falha ao carregar as palavras-passe. Por favor, tente novamente.');
      setLoading(false);
    }
  };

  // Add a new password
  const handleAddPassword = async (newPassword: Omit<PasswordEntry, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      if (!vaultUnlocked) {
        toast({
          title: "Cofre trancado",
          description: "Por favor, desbloqueie o seu cofre antes de adicionar palavras-passe.",
          variant: "destructive"
        });
        return;
      }

      // Get the master key from secure storage
      const masterKey = secureStorage.secureGet('masterKey');
      if (!masterKey) {
        toast({
          title: "Erro",
          description: "Chave mestre não encontrada. Por favor, desbloqueie o seu cofre novamente.",
          variant: "destructive"
        });
        return;
      }

      // Create the password entry with timestamps
      const passwordEntry: PasswordEntry = {
        ...newPassword,
        id: uuidv4(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Add to local state first
      setPasswords(prev => [...prev, passwordEntry]);

      // Save to Firestore
      const userDoc = await getDoc(doc(db, 'users', user!.uid));
      const userData = userDoc.data();

      if (!userData || !userData.vault) {
        throw new Error('Dados do cofre não encontrados');
      }

      // Decrypt existing vault data
      const decryptedData = await cryptoUtils.decryptData(userData.vault, masterKey);
      const vaultData = JSON.parse(decryptedData);

      // Add new password
      vaultData.passwords.push(passwordEntry);

      // Re-encrypt and save
      const encryptedVault = await cryptoUtils.encryptData(JSON.stringify(vaultData), masterKey);
      await updateDoc(doc(db, 'users', user!.uid), {
        vault: encryptedVault,
        updatedAt: new Date().toISOString()
      });

      toast({
        title: "Successo",
        description: "Palavra-passe adicionada com sucesso"
      });
    } catch (error) {
      console.error('Erro ao adicionar a palavra-passe:', error);
      toast({
        title: "Erro",
        description: "Falha ao adicionar a palavra-passe. Por favor, tente novamente.",
        variant: "destructive"
      });
    }
  };

  // Update an existing password
  const handleUpdatePassword = async (id: string, updatedFields: Partial<PasswordEntry>) => {
    try {
      if (!vaultUnlocked) {
        toast({
          title: "Cofre trancado",
          description: "Por favor, desbloqueie o seu cofre antes de atualizar as palavras-passe.",
          variant: "destructive"
        });
        return;
      }

      // Get the master key from secure storage
      const masterKey = secureStorage.secureGet('masterKey');
      if (!masterKey) {
        toast({
          title: "Erro",
          description: "Chave mestre não encontrada. Por favor, desbloqueie o seu cofre novamente.",
          variant: "destructive"
        });
        return;
      }

      // Get the user's vault data
      const userDoc = await getDoc(doc(db, 'users', user!.uid));
      const userData = userDoc.data();

      if (!userData || !userData.vault) {
        throw new Error('Dados do cofre não encontrados');
      }

      // Decrypt existing vault data
      const decryptedData = await cryptoUtils.decryptData(userData.vault, masterKey);
      const vaultData = JSON.parse(decryptedData);

      // Update the password in the vault data
      const passwordIndex = vaultData.passwords.findIndex((p: PasswordEntry) => p.id === id);
      if (passwordIndex === -1) {
        throw new Error('palavra-passe não encontrada');
      }

      vaultData.passwords[passwordIndex] = {
        ...vaultData.passwords[passwordIndex],
        ...updatedFields,
        updatedAt: new Date().toISOString()
      };

      // Re-encrypt and save
      const encryptedVault = await cryptoUtils.encryptData(JSON.stringify(vaultData), masterKey);
      await updateDoc(doc(db, 'users', user!.uid), {
        vault: encryptedVault,
        updatedAt: new Date().toISOString()
      });

      // Update local state
      setPasswords(prev => prev.map(password => 
        password.id === id 
          ? { ...password, ...updatedFields, updatedAt: new Date().toISOString() }
          : password
      ));

      toast({
        title: "Successo",
        description: "Palavra-passe atualizada com sucesso"
      });
    } catch (error) {
      console.error('Erro ao atualizar a palavra-passe:', error);
      toast({
        title: "Erro",
        description: "Falha ao atualizar a palavra-passe. Por favor, tente novamente.",
        variant: "destructive"
      });
    }
  };

  // Delete a password
  const handleDeletePassword = (id: string) => {
    setConfirmDelete(id);
  };

  const confirmDeletePassword = async () => {
    if (!confirmDelete || !user) return;
    
    try {
      if (!vaultUnlocked) {
        toast({
          title: "Cofre trancado",
          description: "Por favor, desbloqueie o seu cofre antes de eliminar as palavras-passe.",
          variant: "destructive"
        });
        return;
      }

      // Get the master key from secure storage
      const masterKey = secureStorage.secureGet('masterKey');
      if (!masterKey) {
        toast({
          title: "Erro",
          description: "Chave mestre não encontrada. Por favor, desbloqueie o seu cofre novamente.",
          variant: "destructive"
        });
        return;
      }

      // Get the user's vault data
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();

      if (!userData || !userData.vault) {
        throw new Error('Vault data not found');
      }

      // Decrypt existing vault data
      const decryptedData = await cryptoUtils.decryptData(userData.vault, masterKey);
      const vaultData = JSON.parse(decryptedData);

      // Remove the password from the vault data
      vaultData.passwords = vaultData.passwords.filter((p: PasswordEntry) => p.id !== confirmDelete);

      // Re-encrypt and save
      const encryptedVault = await cryptoUtils.encryptData(JSON.stringify(vaultData), masterKey);
      await updateDoc(doc(db, 'users', user.uid), {
        vault: encryptedVault,
        updatedAt: new Date().toISOString()
      });

      // Update local state
      setPasswords(prev => prev.filter(password => password.id !== confirmDelete));
      
      const passwordToDelete = passwords.find(p => p.id === confirmDelete);
      setConfirmDelete(null);
      
      toast({
        title: "Successo",
        description: `"${passwordToDelete?.title || 'Password'}" foi eliminado`
      });
    } catch (error) {
      console.error('Erro ao eliminar a palavra-passe:', error);
      toast({
        title: "Erro",
        description: "Falha ao eliminar a palavra-passe. Por favor, tente novamente.",
        variant: "destructive"
      });
    }
  };

  // Function to decrypt a password for viewing
  const decryptPassword = async (id: string) => {
    if (!vaultUnlocked) {
      return "";
    }
    
    // If already decrypted, return from cache
    if (decryptedPasswords[id]) {
      return decryptedPasswords[id];
    }
    
    try {
      // Get the master key from secure storage
      const masterKey = secureStorage.secureGet('masterKey');
      if (!masterKey) {
        console.error("Não há chave mestre disponível");
        return "";
      }

      // Find the entry
      const entry = passwords.find(entry => entry.id === id);
      if (!entry) {
        console.error("Entrada de palavra-passe não encontrada:", id);
        return "";
      }
      
      // Cache the decrypted password
      setDecryptedPasswords(prev => ({
        ...prev,
        [id]: entry.password
      }));
      
      return entry.password;
    } catch (error) {
      console.error("Falha ao descriptografar a palavra-passe:", error);
      return "";
    }
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!vaultUnlocked) {
    return (
      <div className="container mx-auto p-3 sm:p-4 max-w-md">
        <div className="bg-card rounded-lg shadow-md p-4 sm:p-6 border border-border">
          <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-center">Desbloqueie o seu cofre</h2>
          <p className="text-muted-foreground mb-4 sm:mb-6 text-center text-sm sm:text-base">
            Digite a palavra-passe da sua conta para acessar as suas palavra-passes
          </p>
          <MasterkeyForm onSuccess={() => {
            console.log('Cofre desbloqueado com sucesso');
            setLoading(true);
          }} />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-64px)]">
        <div className="animate-pulse-slow text-center">
          <span className="text-lg">A descodificar o seu cofre...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-64px)]">
        <div className="text-center max-w-md">
          <h2 className="text-xl text-destructive mb-2">Erro</h2>
          <p className="mb-4">{error}</p>
          <button 
            className="px-4 py-2 bg-primary text-white rounded-md"
            onClick={() => window.location.reload()}
          >
            Tente novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-3 sm:p-4 max-w-6xl">
      <div className="bg-card rounded-lg shadow-md p-4 sm:p-6 border border-border">
        <h1 className="text-2xl sm:text-3xl font-bold mb-6">As suas palavras-passe</h1>
        
        {error && (
          <div className="bg-destructive/15 text-destructive rounded-md p-3 mb-4">
            {error}
          </div>
        )}
        
        {loading ? (
          <div className="flex items-center justify-center h-[50vh]">
            <div className="flex flex-col items-center">
              <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full mb-3"></div>
              <p className="text-muted-foreground">A carregar as suas palavras-passe...</p>
            </div>
          </div>
        ) : (
          <PasswordList
            passwords={passwords}
            onAddPassword={handleAddPassword}
            onUpdatePassword={handleUpdatePassword}
            onDeletePassword={handleDeletePassword}
            decryptPassword={decryptPassword}
          />
        )}
      </div>
      
      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <AlertDialogContent aria-describedby="delete-dialog-description">
          <AlertDialogHeader>
            <AlertDialogTitle>Apagar a palavra-passe?</AlertDialogTitle>
            <AlertDialogDescription id="delete-dialog-description">
              Isso irá eliminar permanentemente esta palavra-passe do seu cofre. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeletePassword} className="bg-destructive hover:bg-destructive/90">
              Apagar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Vault;
